import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Edit2, 
  Trash2, 
  Share2, 
  MapPin, 
  Calendar,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useStore } from '@/store/useStore';
import { formatDateTime, calculateAge } from '@/utils/dateUtils';

export default function MemoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { memories, children, tags, deleteMemory, toggleFavorite } = useStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [previewMediaIndex, setPreviewMediaIndex] = useState<number | null>(null);

  const memory = memories.find(m => m.id === id);
  
  if (!memory) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-400">
        <p>记录不存在</p>
        <button 
          onClick={() => navigate(-1)}
          className="mt-4 text-primary-500"
        >
          返回
        </button>
      </div>
    );
  }

  const child = children.find(c => c.id === memory.childId);
  const memoryTags = tags.filter(t => memory.tags.includes(t.id));

  const handleDelete = () => {
    deleteMemory(memory.id);
    navigate(-1);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${child?.name}的记录`,
          text: memory.content,
        });
      } catch {
        // 用户取消分享
      }
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* 媒体预览 */}
      {memory.media.length > 0 && (
        <div className="relative bg-gray-100">
          {memory.media[0].type === 'image' ? (
            <img 
              src={memory.media[0].url} 
              alt="" 
              className="w-full aspect-square object-cover"
              onClick={() => setPreviewMediaIndex(0)}
            />
          ) : (
            <video 
              src={memory.media[0].url}
              controls
              className="w-full aspect-square object-cover"
            />
          )}
          
          {/* 媒体计数 */}
          {memory.media.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              1 / {memory.media.length}
            </div>
          )}
        </div>
      )}

      {/* 内容区域 */}
      <div className="px-4 py-5">
        {/* 头部信息 */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: child?.color || '#ccc' }}
            >
              {child?.name.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-800">{child?.name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                {formatDateTime(memory.createdAt)}
              </div>
              {child?.birthDate && child.type !== 'family' && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {calculateAge(child.birthDate, memory.createdAt)}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-1">
            <button
              onClick={() => toggleFavorite(memory.id)}
              className="p-2.5 rounded-full hover:bg-gray-100"
            >
              <Heart 
                className={`w-6 h-6 ${
                  memory.isFavorite 
                    ? 'fill-red-500 text-red-500' 
                    : 'text-gray-400'
                }`} 
              />
            </button>
          </div>
        </div>

        {/* 标签 */}
        {memoryTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {memoryTags.map(tag => (
              <span 
                key={tag.id}
                className="px-3 py-1 rounded-full text-sm"
                style={{ 
                  backgroundColor: `${tag.color}20`,
                  color: tag.color 
                }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* 内容 */}
        <div className="text-gray-700 leading-relaxed text-base whitespace-pre-wrap mb-6">
          {memory.content}
        </div>

        {/* 位置 */}
        {memory.location && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 p-3 bg-gray-50 rounded-xl">
            <MapPin className="w-4 h-4" />
            {memory.location}
          </div>
        )}

        {/* 所有媒体 */}
        {memory.media.length > 1 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">媒体文件</h3>
            <div className="grid grid-cols-3 gap-2">
              {memory.media.map((media, idx) => (
                <div 
                  key={media.id}
                  onClick={() => setPreviewMediaIndex(idx)}
                  className="aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
                >
                  {media.type === 'image' ? (
                    <img 
                      src={media.url} 
                      alt={media.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video 
                      src={media.url}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => navigate(`/edit/${memory.id}`)}
            className="flex items-center justify-center gap-2 py-3 bg-gray-100 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            编辑
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 py-3 bg-gray-100 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            分享
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center justify-center gap-2 py-3 bg-red-50 rounded-xl text-red-500 font-medium hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            删除
          </button>
        </div>
      </div>

      {/* 删除确认 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm animate-slide-up">
            <h3 className="text-lg font-bold text-gray-800 mb-2">确认删除?</h3>
            <p className="text-gray-500 mb-6">删除后无法恢复，请谨慎操作</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-gray-100 rounded-xl font-medium text-gray-700 hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-3 bg-red-500 rounded-xl font-medium text-white hover:bg-red-600"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 媒体预览模态框 */}
      {previewMediaIndex !== null && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={() => setPreviewMediaIndex(null)}
        >
          {/* 顶部导航 */}
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={() => setPreviewMediaIndex(null)}
              className="p-2 rounded-full bg-white/10 text-white"
            >
              <X className="w-6 h-6" />
            </button>
            <span className="text-white text-sm">
              {previewMediaIndex + 1} / {memory.media.length}
            </span>
            <div className="w-10" />
          </div>

          {/* 媒体内容 */}
          <div className="flex-1 flex items-center justify-center">
            {memory.media[previewMediaIndex].type === 'image' ? (
              <img 
                src={memory.media[previewMediaIndex].url} 
                alt=""
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <video 
                src={memory.media[previewMediaIndex].url}
                controls
                className="max-w-full max-h-full"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>

          {/* 左右切换 */}
          {memory.media.length > 1 && (
            <>
              {previewMediaIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewMediaIndex(previewMediaIndex - 1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 text-white"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
              )}
              {previewMediaIndex < memory.media.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewMediaIndex(previewMediaIndex + 1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 text-white"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
